import clsx from 'clsx';
import {useCallback} from 'react';

import AemImage from '@/components/AemImage/AemImage';
import BackButton from '@/components/BackButton/BackButton';
import {ScrollArea} from '@/components/ScrollArea/ScrollArea';
import type {TipsItem} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';
import {TIPS_CARD_SIZES} from '@/types/content';

import TipsCard from '../components/TipsCard';
import useTipsGuideContent from '../hooks/useTipsGuideContent';
import {useTipsListScrollRefs, useTipsListScrollRestore} from '../hooks/useTipsListScrollRestore';
import {getTipsCardConfig} from '../tipsCardConfig';

type TipsListCardProps = {
    card: TipsItem;
    onCardSelect: (card: TipsItem) => void;
};

const TipsListCard = ({card, onCardSelect}: TipsListCardProps) => {
    const {size, borderless} = getTipsCardConfig(card.type);

    const handleClick = useCallback(() => {
        eventMessagesService.trackAmplitude(card.trackingValue);
        onCardSelect(card);
    }, [card, onCardSelect]);

    return (
        <TipsCard
            title={card.title}
            imageMedia={card.images}
            size={size}
            borderless={borderless}
            className={clsx(size === TIPS_CARD_SIZES.BIG && 'col-span-2 w-full')}
            onClick={handleClick}
        />
    );
};

type TipsListViewProps = {
    onCardSelect: (card: TipsItem) => void;
    isEmbedded: boolean;
};

const TipsListView = ({onCardSelect, isEmbedded}: TipsListViewProps) => {
    const {title, description, cards, backgroundImage} = useTipsGuideContent();
    const {scrollTopRef: listScrollTopRef, viewportRef: listViewportRef} = useTipsListScrollRefs();

    useTipsListScrollRestore(listViewportRef, listScrollTopRef);

    const handleBackClick = () => {
        eventMessagesService.sendBackButtonTriggerMessage();
    };

    return (
        <ScrollArea
            viewportRef={listViewportRef}
            className={clsx(
                'relative h-full w-full text-tips-text-primary',
                isEmbedded ? 'bg-tips-surface-primary' : 'bg-tips-surface'
            )}
            contentClassName={clsx('relative', isEmbedded ? 'px-8 pt-8 pb-8' : 'px-4 pt-safe-offset-4 pb-4')}
        >
            <AemImage
                imageMedia={backgroundImage}
                className='pointer-events-none absolute inset-x-0 top-0 z-0 h-full w-full'
                pictureClassName='block w-full'
                imgClassName='h-auto w-full object-cover'
            />
            <div className='relative z-10'>
                <BackButton
                    onClick={handleBackClick}
                    className={clsx(isEmbedded ? 'mb-8 bg-tips-back-button-surface' : 'mb-4')}
                />
                <div className={isEmbedded ? 'mb-8' : 'mb-6'}>
                    <h1 className={clsx('mt-0 mb-2 font-bold', isEmbedded ? 'text-3xl md:text-2xl' : 'text-3xl')}>
                        {title}
                    </h1>
                    <p
                        className={clsx('text-base', isEmbedded ? 'max-w-none' : 'max-w-xl')}
                        dangerouslySetInnerHTML={{__html: description ?? ''}}
                    />
                </div>

                <div className={clsx('grid grid-cols-2', isEmbedded ? 'gap-2 md:gap-3' : 'gap-2')}>
                    {cards.map((card, index) => (
                        <TipsListCard key={index} card={card as TipsItem} onCardSelect={onCardSelect} />
                    ))}
                </div>
            </div>
        </ScrollArea>
    );
};

export default TipsListView;
